import React from "react";
import * as XLSX from "xlsx";
import { useSnackbar } from "notistack";
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";

import { useFetchData } from "../../components/hooks/useFetchData";
import { LoadingBackdrop } from "../../components/common/LoadingBackdrop";

const HEADER_MAP = {
  name: "name",
  "full name": "name",
  "candidate name": "name",
  "lead name": "name",
  email: "email",
  "e-mail": "email",
  "email id": "email",
  "email address": "email",
  whatsapp: "whatsapp",
  "what's app": "whatsapp",
  "contact no": "contactNo",
  "contact no.": "contactNo",
  "contact number": "contactNo",
  phone: "contactNo",
  "phone number": "contactNo",
  mobile: "contactNo",
  "mobile no": "contactNo",
  "mobile number": "contactNo",
  "preferred country": "preferredCountry",
  "preferred course": "preferredCourse",
  qualification: "qualification",
  "convenient time to contact": "convenientTimeToContact",
  "convienent time to contact": "convenientTimeToContact",
  city: "city",
  remarks: "remarks",
};

const normalizeHeader = (header = "") =>
  String(header).trim().toLowerCase().replace(/\s+/g, " ");

const isExcelTimeFraction = (value) =>
  typeof value === "number" && !Number.isNaN(value) && value > 0 && value < 1;

const formatExcelTimeFraction = (fraction) => {
  const totalMinutes = Math.round(fraction * 24 * 60);
  const hours = Math.floor(totalMinutes / 60) % 24;
  const minutes = totalMinutes % 60;
  return `${hours}:${String(minutes).padStart(2, "0")}`;
};

const formatCellValue = (value) => {
  if (value === null || value === undefined) return "";

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const hours = value.getHours();
    const minutes = value.getMinutes();
    if (hours === 0 && minutes === 0) return "";
    return `${hours}:${String(minutes).padStart(2, "0")}`;
  }

  if (typeof value === "number" && !Number.isNaN(value)) {
    if (isExcelTimeFraction(value)) {
      return formatExcelTimeFraction(value);
    }

    if (value >= 1) {
      const fraction = value % 1;
      if (fraction > 0 && fraction < 1) {
        return formatExcelTimeFraction(fraction);
      }
    }

    return String(value).trim();
  }

  const text = String(value).trim();
  if (!text) return "";

  const numeric = Number(text);
  if (text === String(numeric) && isExcelTimeFraction(numeric)) {
    return formatExcelTimeFraction(numeric);
  }

  return text;
};

const cleanValue = (value) => formatCellValue(value);

const getFirstName = (fullName = "") => {
  const parts = cleanValue(fullName).split(/\s+/).filter(Boolean);
  return parts[0] || "";
};

const resolveContactNo = (contactNo, whatsapp) => {
  const contact = cleanValue(contactNo);
  const wa = cleanValue(whatsapp);
  return contact || wa;
};

const getMappedFieldsFromHeaders = (sheetRows = []) => {
  const fields = new Set();
  const headerSource = sheetRows[0] || {};

  Object.keys(headerSource).forEach((rawHeader) => {
    const key = HEADER_MAP[normalizeHeader(rawHeader)];
    if (key) fields.add(key);
  });

  return fields;
};

const buildImportRows = (sheetRows = []) => {
  const presentFields = getMappedFieldsFromHeaders(sheetRows);

  const mappedRows = sheetRows
    .map((row) => {
      const transformed = {};
      presentFields.forEach((field) => {
        transformed[field] = "";
      });

      Object.keys(row || {}).forEach((rawHeader) => {
        const normalized = normalizeHeader(rawHeader);
        const key = HEADER_MAP[normalized];
        if (key) {
          transformed[key] = cleanValue(row[rawHeader]);
        }
      });

      transformed.name = getFirstName(transformed.name);
      transformed.contactno = resolveContactNo(
        transformed.contactNo,
        transformed.whatsapp
      );
      delete transformed.contactNo;
      delete transformed.whatsapp;

      return transformed;
    })
    .filter((row) =>
      Object.values(row).some((value) => String(value || "").trim() !== "")
    );

  return mappedRows;
};

const getResultVariant = (item) => {
  const status = String(item?.status || "").toLowerCase();
  const message = String(item?.message || "").toLowerCase();

  if (
    status === "success" ||
    status === "created" ||
    message.includes("success")
  ) {
    return "success";
  }

  if (
    message.includes("already existing") ||
    message.includes("already exist")
  ) {
    return "warning";
  }

  return "error";
};

const getResultLabel = (item) => {
  const variant = getResultVariant(item);
  if (variant === "success") return "Success";
  if (variant === "warning") return "Already existing";
  return "Failed";
};

const getSummaryMessage = (result) => {
  const summary = result?.data || {};
  const created = Number(summary.created ?? 0);
  const existing = Number(summary.existing ?? 0);
  const failed = Number(summary.failed ?? 0);
  const total = Number(summary.total ?? 0);

  const parts = [];
  if (created > 0) parts.push(`${created} created`);
  if (existing > 0) parts.push(`${existing} already existing`);
  if (failed > 0) parts.push(`${failed} failed`);

  if (parts.length > 0) {
    return `${result?.message || "Import completed"} (${total} total: ${parts.join(", ")})`;
  }

  return result?.message || "Import completed.";
};

const getSummarySeverity = (result) => {
  const summary = result?.data || {};
  const created = Number(summary.created ?? 0);
  const failed = Number(summary.failed ?? 0);

  if (failed > 0 && created === 0) return "error";
  if (created > 0 && failed === 0) return "success";
  return "info";
};

const ImportExcelPage = () => {
  const inputRef = React.useRef(null);
  const [fileName, setFileName] = React.useState("");
  const [rows, setRows] = React.useState([]);
  const [importResult, setImportResult] = React.useState(null);

  const { fetchData, loading } = useFetchData();
  const { enqueueSnackbar } = useSnackbar();

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    setImportResult(null);

    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      enqueueSnackbar("Please upload a valid .xlsx file.", {
        variant: "error",
      });
      setRows([]);
      setFileName("");
      event.target.value = "";
      return;
    }

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const firstSheet = workbook.SheetNames?.[0];

      if (!firstSheet) {
        enqueueSnackbar("Uploaded file is empty.", { variant: "error" });
        return;
      }

      const worksheet = workbook.Sheets[firstSheet];
      const jsonRows = XLSX.utils.sheet_to_json(worksheet, {
        defval: "",
        raw: false,
      });
      const transformedRows = buildImportRows(jsonRows);

      if (!transformedRows.length) {
        enqueueSnackbar("No valid rows found in uploaded sheet.", {
          variant: "warning",
        });
      }

      setRows(transformedRows);
      setFileName(file.name);
    } catch (error) {
      enqueueSnackbar("Failed to parse Excel file.", { variant: "error" });
      setRows([]);
      setFileName("");
    } finally {
      event.target.value = "";
    }
  };

  const handleImport = async () => {
    if (!rows.length) {
      enqueueSnackbar("Please upload an Excel file with valid rows.", {
        variant: "warning",
      });
      return;
    }

    const result = await fetchData("importleadsexcel", {
      method: "post",
      body: { leads: rows },
    });

    if (result?.code === 200 && result?.success !== false) {
      const message = getSummaryMessage(result);
      const severity = getSummarySeverity(result);
      const snackbarVariant =
        severity === "error"
          ? "error"
          : severity === "success"
            ? "success"
            : "info";

      setImportResult({
        severity,
        message,
        summary: result.data || {},
        results: result.data?.results || [],
      });
      enqueueSnackbar(message, { variant: snackbarVariant });
      return;
    }

    const message = result?.message || "Excel import failed.";
    setImportResult({
      severity: "error",
      message,
      summary: null,
      results: [],
    });
    enqueueSnackbar(message, { variant: "error" });
  };

  return (
    <>
      <Box display="flex" flexDirection="column" width="100%" height="100%" rowGap={2}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          rowGap={2}
        >
          <Typography fontWeight={700} fontSize={24} color="#222222">
            Import Excel
          </Typography>
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined"
              startIcon={<UploadFileOutlinedIcon />}
              sx={{ borderRadius: "12px" }}
              onClick={() => inputRef.current?.click()}
            >
              Upload .xlsx
            </Button>
            <Button
              variant="contained"
              sx={{ borderRadius: "12px" }}
              disabled={!rows.length}
              onClick={handleImport}
            >
              Import Leads
            </Button>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx"
              style={{ display: "none" }}
              onChange={handleFileSelect}
            />
          </Stack>
        </Stack>

        <Typography variant="body2" color="text.secondary">
          Upload an Excel file with columns such as Full Name, Contact No., WhatsApp,
          Preferred Country, Preferred Course, Qualification, Convenient time to Contact,
          city, Remarks. Contact No. is used as the phone when present; otherwise WhatsApp.
          The first word of Full Name is used as the lead name.
        </Typography>

        {fileName ? (
          <Typography variant="body2" fontWeight={500}>
            Selected file: {fileName}
          </Typography>
        ) : null}

        {importResult ? (
          <Alert severity={importResult.severity}>{importResult.message}</Alert>
        ) : null}

        {importResult?.summary ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label={`Total: ${importResult.summary.total ?? 0}`} />
            <Chip
              label={`Created: ${importResult.summary.created ?? 0}`}
              color="success"
              variant="outlined"
            />
            <Chip
              label={`Existing: ${importResult.summary.existing ?? 0}`}
              color="warning"
              variant="outlined"
            />
            <Chip
              label={`Failed: ${importResult.summary.failed ?? 0}`}
              color="error"
              variant="outlined"
            />
          </Stack>
        ) : null}

        {importResult?.results?.length > 0 ? (
          <TableContainer component={Paper}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ p: 2, pb: 0 }}>
              Import results
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#E4EBF4" }}>
                  <TableCell>Contact No.</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Lead No</TableCell>
                  <TableCell>Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {importResult.results.map((item, index) => {
                  const variant = getResultVariant(item);
                  const chipColor =
                    variant === "success"
                      ? "success"
                      : variant === "warning"
                        ? "warning"
                        : "error";
                  const contactNo =
                    item.contactno || item.contactNo || item.whatsapp;

                  return (
                    <TableRow key={`${contactNo}-${index}`}>
                      <TableCell>{contactNo || "-"}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={getResultLabel(item)}
                          color={chipColor}
                        />
                      </TableCell>
                      <TableCell>{item.leadNo ?? "-"}</TableCell>
                      <TableCell>{item.message || "-"}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : null}

        {!importResult ? (
          <TableContainer component={Paper} sx={{ mt: 1 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ p: 2, pb: 0 }}>
              Preview ({rows.length} row{rows.length === 1 ? "" : "s"})
            </Typography>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "#3A80F7" }}>
                  <TableCell sx={{ color: "white" }}>Name</TableCell>
                  <TableCell sx={{ color: "white" }}>Email</TableCell>
                  <TableCell sx={{ color: "white" }}>Contact No.</TableCell>
                  <TableCell sx={{ color: "white" }}>Preferred Country</TableCell>
                  <TableCell sx={{ color: "white" }}>Preferred Course</TableCell>
                  <TableCell sx={{ color: "white" }}>Qualification</TableCell>
                  <TableCell sx={{ color: "white" }}>Convenient time to Contact</TableCell>
                  <TableCell sx={{ color: "white" }}>City</TableCell>
                  <TableCell sx={{ color: "white" }}>Remarks</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length > 0 ? (
                  rows.map((row, index) => (
                    <TableRow key={`${row.contactno || "row"}-${index}`}>
                      <TableCell>{row.name || "-"}</TableCell>
                      <TableCell>{row.email || "-"}</TableCell>
                      <TableCell>{row.contactno || "-"}</TableCell>
                      <TableCell>{row.preferredCountry || "-"}</TableCell>
                      <TableCell>{row.preferredCourse || "-"}</TableCell>
                      <TableCell>{row.qualification || "-"}</TableCell>
                      <TableCell>{row.convenientTimeToContact || "-"}</TableCell>
                      <TableCell>{row.city || "-"}</TableCell>
                      <TableCell>{row.remarks || "-"}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      Upload a file to preview rows.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : null}
      </Box>
      <LoadingBackdrop isLoading={loading["importleadsexcel"]} />
    </>
  );
};

export default ImportExcelPage;
